/* ==========================================================================
   Royal Care Professional — newsletter popup
   sections/rc-newsletter-popup.liquid

   Decides whether this visitor sees the popup, and runs the dialog when they
   do. What it remembers lives in localStorage:

     rc-nl-subscribed  set once a sign-up has posted back successfully — the
                       popup never opens on its own again
     rc-nl-until       a timestamp; closing or submitting keeps it away
                       until then

   It never stacks on another dialog. If the cart, search, quick add or the
   mobile menu owns the screen when the timer fires, it waits and retries.
   ========================================================================== */

(function () {
  'use strict';

  if (window.RCNewsletterPopup) {
    window.RCNewsletterPopup.init();
    return;
  }

  var DAY = 86400000;
  var CLOSE_MS = 240;
  var RETRY_MS = 5000;

  function read(key) {
    try {
      return window.localStorage.getItem('rc-nl-' + key);
    } catch (e) {
      return null;
    }
  }

  function write(key, value) {
    try {
      window.localStorage.setItem('rc-nl-' + key, value);
    } catch (e) {
      /* private mode — the popup simply behaves as on a first visit */
    }
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** True when another dialog, or the mobile menu, already has the screen. */
  function screenBusy(own) {
    var open = document.querySelectorAll('dialog[open]');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== own) return true;
    }
    return !!document.querySelector('.rc-nav.is-drawer-open');
  }

  function Popup(root) {
    this.root = root;
    this.dialog = root.querySelector('[data-rc-nl-dialog]');
    this.designMode = root.dataset.designMode === 'true';
    this.days = parseFloat(root.dataset.days) || 7;
    this.shown = false;
    this.timer = 0;
    this.closeTimer = 0;
  }

  Popup.prototype.remember = function () {
    if (!this.designMode) write('until', String(Date.now() + this.days * DAY));
  };

  /*
   * The sheet loads with media="print" so it never blocks render. Opening
   * before it applies would flash an unstyled dialog, so wait for it.
   */
  Popup.prototype.whenStyled = function (callback) {
    var link = this.root.querySelector('[data-rc-nl-css]');
    if (!link || link.media !== 'print') {
      callback();
      return;
    }
    link.addEventListener('load', callback, { once: true });
  };

  /**
   * @param {boolean} [force] skip the "is this a good moment" checks — used
   *   for a sign-up result and in the theme editor
   */
  Popup.prototype.open = function (force) {
    var self = this;
    var dialog = this.dialog;
    if (!dialog || dialog.open) return;

    if (!force) {
      if (this.shown) return;
      if (screenBusy(dialog)) {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(function () {
          self.open();
        }, RETRY_MS);
        return;
      }
    }

    this.shown = true;
    window.clearTimeout(this.timer);

    this.whenStyled(function () {
      if (dialog.open) return;
      window.clearTimeout(self.closeTimer);
      dialog.classList.remove('is-closing');

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        dialog.classList.add('is-fallback');
      }

      // Horizon's scroll lock. It covers .page-wrapper, which is the page's
      // scroller on desktop, where locking <body> alone would do nothing.
      document.documentElement.setAttribute('scroll-lock', '');
    });
  };

  Popup.prototype.close = function () {
    var dialog = this.dialog;
    if (!dialog || !dialog.open) return;

    this.remember();
    dialog.classList.add('is-closing');
    window.clearTimeout(this.closeTimer);

    this.closeTimer = window.setTimeout(
      function () {
        dialog.classList.remove('is-closing');
        if (dialog.classList.contains('is-fallback')) {
          dialog.removeAttribute('open');
          dialog.classList.remove('is-fallback');
          document.documentElement.removeAttribute('scroll-lock');
        } else {
          dialog.close();
        }
      },
      reducedMotion() ? 0 : CLOSE_MS
    );
  };

  Popup.prototype.copy = function (button) {
    var code = this.root.querySelector('[data-rc-nl-code]');
    var status = this.root.querySelector('[data-rc-nl-status]');
    if (!code) return;

    var text = code.textContent.trim();
    var label = button.dataset.label || button.textContent;
    var copied = button.dataset.copiedLabel || label;
    button.dataset.label = label;

    function done() {
      button.textContent = copied;
      button.classList.add('is-copied');
      if (status) status.textContent = copied;
      window.setTimeout(function () {
        button.textContent = label;
        button.classList.remove('is-copied');
        if (status) status.textContent = '';
      }, 2000);
    }

    // Without the clipboard API, selecting the code at least leaves it one
    // tap from copying by hand.
    function fallback() {
      var range = document.createRange();
      range.selectNodeContents(code);
      var selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      try {
        if (document.execCommand('copy')) done();
      } catch (e) {
        /* left selected */
      }
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, fallback);
    } else {
      fallback();
    }
  };

  Popup.prototype.start = function () {
    var self = this;
    var root = this.root;
    var dialog = this.dialog;
    if (!dialog) return;

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-rc-nl-close]')) {
        self.close();
        return;
      }
      var copy = event.target.closest('[data-rc-nl-copy]');
      if (copy) self.copy(copy);
    });

    // The card fills the dialog's box, so a click that lands on the <dialog>
    // itself came from the backdrop.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) self.close();
    });

    // Escape: play the closing animation instead of vanishing.
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      self.close();
    });

    // However it closed — including a repeated Escape the browser will not
    // let a page cancel — release the page and remember it.
    dialog.addEventListener('close', function () {
      dialog.classList.remove('is-closing');
      document.documentElement.removeAttribute('scroll-lock');
      self.remember();
    });

    var form = root.querySelector('form');
    if (form) {
      // The page reloads with the result; do not greet the same visitor
      // again on the way there.
      form.addEventListener('submit', function () {
        self.remember();
      });
    }

    // A sign-up has just posted back to this page.
    if (root.querySelector('[data-rc-nl-success]')) {
      write('subscribed', '1');
      this.open(true);
      return;
    }
    if (root.querySelector('[data-rc-nl-error]')) {
      this.open(true);
      return;
    }

    // In the theme editor it opens when the section is selected instead.
    if (this.designMode) return;
    if (read('subscribed') === '1') return;
    if (Number(read('until')) > Date.now()) return;
    if (root.dataset.mobile !== 'true' && window.matchMedia('(max-width: 749px)').matches) return;

    /*
     * The delay counts from the visitor's first interaction — a scroll, tap,
     * key press or mouse movement — not from page load. Opened on a timer from
     * load, the popup landed while the page was still arriving and covered the
     * hero before anyone had seen it; PageSpeed scored exactly that frame as
     * unfinished loading. Someone who has not engaged yet is also the least
     * likely to sign up.
     */
    var delay = Math.max(0, parseFloat(root.dataset.delay) || 0) * 1000;
    var engagement = ['scroll', 'wheel', 'pointerdown', 'touchstart', 'keydown', 'mousemove'];
    var listen = { capture: true, passive: true };
    var onEngage = function () {
      engagement.forEach(function (name) {
        document.removeEventListener(name, onEngage, listen);
      });
      self.timer = window.setTimeout(function () {
        self.open();
      }, delay);
    };
    // Capture phase, because on desktop Horizon scrolls .page-wrapper rather
    // than the window, and scroll events do not bubble.
    engagement.forEach(function (name) {
      document.addEventListener(name, onEngage, listen);
    });

    if (root.dataset.exitIntent === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var onLeave = function (event) {
        // Out through the top edge, towards the tabs or the address bar.
        if (event.relatedTarget || event.clientY > 0) return;
        document.removeEventListener('mouseout', onLeave);
        self.open();
      };
      document.addEventListener('mouseout', onLeave);
    }
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-rc-nl]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.rcPopup) return;
      root.rcPopup = new Popup(root);
      root.rcPopup.start();
    });
  }

  function popupIn(target) {
    var root = target && target.querySelector ? target.querySelector('[data-rc-nl]') : null;
    return root ? root.rcPopup : null;
  }

  window.RCNewsletterPopup = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: open while the section is selected, and keep it open when
  // a settings change re-renders it.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    var popup = popupIn(event.target);
    if (popup) popup.open(true);
  });

  document.addEventListener('shopify:section:select', function (event) {
    var popup = popupIn(event.target);
    if (popup) popup.open(true);
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    var popup = popupIn(event.target);
    if (popup) popup.close();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (popupIn(event.target)) document.documentElement.removeAttribute('scroll-lock');
  });
})();
